/**
 * conflict_map.ts — the World Atlas conflict layers, PURE (arrays in, rgba
 * out — node-verifiable, no conn/DOM).
 *
 * · OWNERSHIP ('civ'): territorial influence of the two civilizations —
 *   every settlement projects tier-weighted influence that falls off with
 *   distance²; a point's color is the DOMINANT civ (Aurelian amber vs
 *   Borean blue), alpha by how contested/settled the ground is. Frontier
 *   between rival influence reads pale; deep heartland reads saturated.
 * · WAR ('war'): LIGHT red where a settlement stands in a DECLARED active
 *   war (the war_state pair), DARK red where versonas are actually
 *   fighting RIGHT NOW (live war_clash rows — visit one and the battle is
 *   there on the road).
 */

export interface CivSiteSample { x: number; z: number; tier: string; civ: 'a' | 'b' }
export interface WarSiteSample { x: number; z: number; tier: string }
export interface ClashSample { x: number; z: number }

/** Matches the Atlas civ palette (CIV_COLOR a/b) as rgb triplets. */
export const CIV_RGB: Record<'a' | 'b', [number, number, number]> = {
  a: [217, 169, 78],   // Aurelian Concord — amber
  b: [127, 196, 232],  // Borean Compact — ice blue
};
export const WAR_DECLARED_RGB: [number, number, number] = [235, 110, 100]; // light red
export const WAR_CLASH_RGB: [number, number, number] = [150, 12, 18];      // dark blood red

const TIER_WEIGHT: Record<string, number> = { city: 9, town: 3, hamlet: 1, capital: 9, district: 0, circuit: 1 }; // capital wave: a district is its capital's ward
/** Influence softening radius (m) — a city dominates ~its region, not one pixel. */
const SOFTEN_M2 = 900 * 900;
/** A declared war shades this far around each warring settlement (m). */
export const WAR_ZONE_M = 2600;
/** A live clash burns this far around the actual fight (m). */
export const CLASH_ZONE_M = 700;

/** Per-civ influence at a point. */
export function ownershipAt(sites: CivSiteSample[], x: number, z: number): { a: number; b: number } {
  let a = 0, b = 0;
  for (const s of sites) {
    const d2 = (s.x - x) * (s.x - x) + (s.z - z) * (s.z - z);
    const inf = (TIER_WEIGHT[s.tier] ?? 1) / (1 + d2 / SOFTEN_M2);
    if (s.civ === 'a') a += inf; else b += inf;
  }
  return { a, b };
}

/** Declared-war and live-clash intensity (0..1 each) at a point. */
export function warAt(warSites: WarSiteSample[], clashes: ClashSample[], x: number, z: number): { declared: number; clash: number } {
  let declared = 0, clash = 0;
  for (const s of warSites) {
    const d = Math.hypot(s.x - x, s.z - z);
    const r = WAR_ZONE_M * ((TIER_WEIGHT[s.tier] ?? 1) >= 9 ? 1.4 : 1);
    if (d < r) declared = Math.max(declared, 1 - d / r);
  }
  for (const c of clashes) {
    const d = Math.hypot(c.x - x, c.z - z);
    if (d < CLASH_ZONE_M) clash = Math.max(clash, 1 - d / CLASH_ZONE_M);
  }
  return { declared, clash };
}

/** The rgba for one sampled point of the active conflict layer. */
export function conflictRgba(
  mode: 'civ' | 'war',
  sites: CivSiteSample[], warSites: WarSiteSample[], clashes: ClashSample[],
  x: number, z: number,
): [number, number, number, number] {
  if (mode === 'civ') {
    const { a, b } = ownershipAt(sites, x, z);
    const total = a + b;
    if (total < 0.02) return [0, 0, 0, 0]; // deep wilds — nobody's ground
    const domA = a >= b;
    const lead = Math.abs(a - b) / total;           // 0 contested → 1 heartland
    const settled = Math.min(1, total / 1.2);       // how strongly held at all
    const [r, g, bb] = CIV_RGB[domA ? 'a' : 'b'];
    const alpha = Math.round(30 + 130 * lead * settled);
    return [r, g, bb, alpha];
  }
  const { declared, clash } = warAt(warSites, clashes, x, z);
  if (clash > 0.02) {
    const [r, g, b] = WAR_CLASH_RGB;
    return [r, g, b, Math.round(90 + 140 * clash)];
  }
  if (declared > 0.02) {
    const [r, g, b] = WAR_DECLARED_RGB;
    return [r, g, b, Math.round(20 + 95 * declared)];
  }
  return [0, 0, 0, 0];
}

/** Fill one image row of the overlay (the sampleWxRow contract). */
export function sampleConflictRow(
  mode: 'civ' | 'war',
  sites: CivSiteSample[], warSites: WarSiteSample[], clashes: ClashSample[],
  row: number, N: number, x0: number, z0: number, w: number, h: number,
  out: Uint8ClampedArray,
): void {
  const z = z0 + ((row + 0.5) / N) * h;
  for (let i = 0; i < N; i++) {
    const x = x0 + ((i + 0.5) / N) * w;
    const [r, g, b, a] = conflictRgba(mode, sites, warSites, clashes, x, z);
    const o = (row * N + i) * 4;
    out[o] = r; out[o + 1] = g; out[o + 2] = b; out[o + 3] = a;
  }
}
